import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import { ProductDetails, RawMaterialDetails } from "../../../sc-models/facility-product-details-dto";
import { dtrackItService } from "../../../../transaction-certificates/tc-service/dtrackit.service";
import useNotification from "../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../shared/app-configs/utilit";

interface RawMaterialsInfoProps {
  productData: ProductDetails;
  isProductNoRequired: boolean;
  rawMaterialInfoData: RawMaterialDetails[]; // Expecting an array
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<RawMaterialDetails[]>
  >;
}

// Define Yup validation schema for each row


const RawMaterialDetailsForm: React.FC<RawMaterialsInfoProps> = ({
  isProductNoRequired,
  productData,
  rawMaterialInfoData,
  setRawMaterialInfoData,
}) => {
  const [data, setData] = useState<RawMaterialDetails[]>(
    rawMaterialInfoData
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<RawMaterialDetails[]>(
    rawMaterialInfoData
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [rawMaterialCodes, setRawMaterialCodes] = useState([]);
  const { notify } = useNotification()
  const { isRestrictPopUpSave, setRestrictPopUpSave,setFPDPrdtRawMtrlCodes } = useTransactionCertificateStore();

  console.log("productData", productData)
  console.log("isProductNoRequired", isProductNoRequired)

  const validationSchema = Yup.object().shape({
    rawMaterialCode: isProductNoRequired ? 
      Yup.string().required("TC Standard is required") : 
      Yup.string().nullable(),
    rawMaterialPercentage: Yup.string()
      .nullable()
      .test(
        'percentage-validation',
        'Total percentage cannot exceed 100',
        function(value) {
          if (!value) return true;
          
          // If current value is a range, skip validation
          if (value.includes('-')) return true;

          // Calculate total of only pure numbers
          if (editingRowIndex > -1) {
            const total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              
              const percentage = item.rawMaterialPercentage;
              // Only add to total if it's a pure number (not a range)
              if (percentage && typeof percentage === 'string' && !percentage.includes('-')) {
                return sum + parseFloat(percentage);
              }
              return sum;
            }, 0);

            const currentValue = parseFloat(value);
            if ((total + currentValue) > 100) {
              notify("Raw Material Percentage cannot be greater than 100", "error");
              return false;
            }
          }
          return true;
        }
      )
  });
  const formik = useFormik<RawMaterialDetails>({
    initialValues: {
      rawMaterialCode: "",
      rawMaterialPercentage: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: RawMaterialDetails = {
      rawMaterialCode: "",
      rawMaterialPercentage: "",
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true)
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;

    // // Determine if the row being edited is new by comparing `editingRowIndex` with the data length
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    setData(tableData);
    setRestrictPopUpSave(false)
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  }

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setRestrictPopUpSave(false)
    setRawMaterialInfoData(newData);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes()
        const rawMaterials = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1}  (${t.code_1})` }))
        setRawMaterialCodes(rawMaterials);
        setFPDPrdtRawMtrlCodes(rawMaterials);
      } catch (err) {
        Utills.cle(`Error in Fetching Organic Farm Standard Code ${err}`)
      }
    };

    getRawMaterialCodes()
  }, [])

  const columns: EditableColumnDef<RawMaterialDetails>[] = [
    {
      accessorKey: "rawMaterialCode",
      header: "Product Raw Material Code",
      Header: isProductNoRequired ? <span> <i style={{ color: 'red' }}> * </i>Product Raw Material Code</span> : <span>Product Raw Material Code</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="rawMaterialCode"
          value={value}
          enableSearch={true}
          options={rawMaterialCodes}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "rawMaterialPercentage",
      header: "Product Raw Material Percentage",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="rawMaterialPercentage"
          value={value}
          type="text"
          inputProps={{ max: 100 }}
          onChange={(e) => {
            const newValue = e.target.value;
            // Only allow digits and one hyphen

            if (!newValue || /^(100|[1-9]?[0-9])(-([1-9]?[0-9]|100)?)?$/.test(newValue)) {
              console.log("newValue", newValue);
              onChange(newValue);
            }
            // }
            // onChange(e.target.value)
          }}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />

      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setRawMaterialInfoData(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={!formik.isValid || isRestrictPopUpSave} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add Raw Material
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enablePagination = {false}
        />
      </Box>
    </div>
  );
};

export default RawMaterialDetailsForm;